import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { FaCamera, FaStar, FaRegStar, FaTrash } from 'react-icons/fa';

import { Container, Cabecalho, Grade, Miniatura, Imagem, Selo, AcoesFoto, Aviso, ImagemAmpliada } from './styles';

import Button from '../Button';
import Modal from '../Modal';

import { IMerFot } from '../../types/MerFot';
import { definirCapa, ehCapa, enviarFoto, excluirFoto, listarFotos, urlFoto } from '../../services/fotos';
import { reduzirImagem } from '../../utils/imagem';

interface FotosProdutoProps {
  codmer?: number | string;
  codbar?: string;
  codpad?: string;
}

const mensagemErro = (error: any, padrao: string) =>
  error?.response?.data?.MENSAGEM || error?.message || padrao;

// Fotos do produto na ficha: envio (ImageShack via API), capa e exclusão.
// Usa um estado de carregando próprio, sem o overlay global, para não travar a ficha.
export default function FotosProduto({ codmer, codbar, codpad }: FotosProdutoProps) {
  const [fotos, setFotos] = useState<IMerFot[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [enviando, setEnviando] = useState('');
  const [ocupado, setOcupado] = useState<number | string>();
  const [ampliada, setAmpliada] = useState<IMerFot>();
  const inputRef = useRef<HTMLInputElement>(null);

  const carregar = async () => {
    if (!codmer) return;
    try {
      setCarregando(true);
      setFotos(await listarFotos(codmer));
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar as fotos do produto');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    setFotos([]);
    carregar();
  }, [codmer]);

  const adicionar = async (arquivos: FileList | null) => {
    if (!codmer || !arquivos?.length) return;
    const lista = Array.from(arquivos);

    // Um de cada vez: a API grava a capa pela primeira foto do produto
    for (let indice = 0; indice < lista.length; indice++) {
      const arquivo = lista[indice];
      setEnviando(lista.length > 1 ? `Enviando ${indice + 1} de ${lista.length}...` : 'Enviando...');
      try {
        const { blob, nome } = await reduzirImagem(arquivo);
        await enviarFoto(codmer, codbar, codpad, blob, nome);
        toast.success(`Foto ${arquivo.name} enviada`);
      } catch (error) {
        console.error(error);
        toast.error(mensagemErro(error, `Erro ao enviar ${arquivo.name}`));
      }
    }

    setEnviando('');
    if (inputRef.current) inputRef.current.value = '';
    carregar();
  };

  const tornarCapa = async (foto: IMerFot) => {
    try {
      setOcupado(foto.cod);
      await definirCapa(foto.cod);
      await carregar();
    } catch (error) {
      console.error(error);
      toast.error(mensagemErro(error, 'Erro ao definir a capa'));
    } finally {
      setOcupado(undefined);
    }
  };

  const excluir = async (foto: IMerFot) => {
    if (!window.confirm('Excluir esta foto do produto?')) return;
    try {
      setOcupado(foto.cod);
      await excluirFoto(foto.cod);
      toast.success('Foto excluída');
      setAmpliada(undefined);
      await carregar();
    } catch (error) {
      console.error(error);
      toast.error(mensagemErro(error, 'Erro ao excluir a foto'));
    } finally {
      setOcupado(undefined);
    }
  };

  // A foto só pode ser ligada a um produto já gravado
  if (!codmer) {
    return (
      <Container>
        <Cabecalho><h2>Fotos</h2></Cabecalho>
        <Aviso>Salve o produto para adicionar fotos.</Aviso>
      </Container>
    );
  }

  return (
    <Container>
      <Cabecalho>
        <h2>Fotos{fotos.length > 0 && ` (${fotos.length})`}</h2>
        <Button
          variante='secundario'
          tamanho='sm'
          icone={<FaCamera />}
          texto={enviando || 'Adicionar fotos'}
          carregando={!!enviando}
          onClick={() => inputRef.current?.click()}
        />
        <input
          ref={inputRef}
          type='file'
          accept='image/*'
          multiple
          hidden
          onChange={e => adicionar(e.target.files)}
        />
      </Cabecalho>

      {carregando && fotos.length === 0 && <Aviso>Carregando fotos...</Aviso>}
      {!carregando && fotos.length === 0 && <Aviso>Nenhuma foto cadastrada.</Aviso>}

      {fotos.length > 0 &&
        <Grade>
          {fotos.map(foto => (
            <Miniatura key={foto.cod} $capa={ehCapa(foto)}>
              <Imagem
                src={urlFoto(foto.linfot)}
                alt={`Foto ${foto.cod}`}
                loading='lazy'
                onClick={() => setAmpliada(foto)}
              />
              {ehCapa(foto) && <Selo><FaStar /> Capa</Selo>}
              <AcoesFoto>
                <Button
                  variante='fantasma'
                  tamanho='sm'
                  icone={ehCapa(foto) ? <FaStar /> : <FaRegStar />}
                  title={ehCapa(foto) ? 'Foto de capa' : 'Definir como capa'}
                  aria-label={ehCapa(foto) ? 'Foto de capa' : 'Definir como capa'}
                  disabled={ehCapa(foto) || ocupado !== undefined}
                  carregando={ocupado === foto.cod}
                  onClick={() => tornarCapa(foto)}
                />
                <Button
                  variante='fantasma'
                  tamanho='sm'
                  icone={<FaTrash />}
                  title='Excluir foto'
                  aria-label='Excluir foto'
                  disabled={ocupado !== undefined}
                  onClick={() => excluir(foto)}
                />
              </AcoesFoto>
            </Miniatura>
          ))}
        </Grade>}

      {ampliada &&
        <Modal
          titulo={ehCapa(ampliada) ? 'Foto de capa' : 'Foto do produto'}
          onClose={() => setAmpliada(undefined)}
          rodape={
            <>
              {!ehCapa(ampliada) &&
                <Button variante='secundario' icone={<FaStar />} texto='Definir como capa'
                  onClick={() => { tornarCapa(ampliada); setAmpliada(undefined); }} />}
              <Button variante='perigo' icone={<FaTrash />} texto='Excluir' onClick={() => excluir(ampliada)} />
            </>
          }
        >
          <ImagemAmpliada src={urlFoto(ampliada.linfot)} alt={`Foto ${ampliada.cod}`} />
        </Modal>}
    </Container>
  );
}
